import { ADMIN_API_BASE_URL } from '../config'
import { handleApiResponse } from '../utils/commonfunction'
import {
  makeApiRequest,
  addAppTypeToParams,
  addAppTypeToBody,
} from '../utils/apiHelper'

export interface Crypto {
  id: string
  symbol: string
  name: string
  symbol_id: string
  image?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CryptoUniverse {
  id: string
  name: string
  description?: string
  coin_symbols?: string[]
  Crypto_ids: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CryptoListResponse {
  coins: Crypto[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

export interface CryptoUniverseListResponse {
  universes: CryptoUniverse[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

class CryptoService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('adminToken')
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  }

  // Crypto endpoints
  async getCryptos(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    exchange?: string,
    sector?: string,
    active?: boolean
  ): Promise<CryptoListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    })

    if (search) params.append('search', search)
    if (exchange) params.append('exchange', exchange)
    if (sector) params.append('sector', sector)
    if (active !== undefined) params.append('active', active.toString())

    const paramsWithAppType = addAppTypeToParams(params)

    return makeApiRequest(`/crypto/coins?${paramsWithAppType}`, {
      headers: this.getAuthHeaders(),
    })
  }

  async createCrypto(CryptoData: Partial<Crypto>): Promise<Crypto> {
    const bodyWithAppType = addAppTypeToBody(CryptoData)
    return makeApiRequest('/crypto/coins', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bodyWithAppType),
    })
  }

  async updateCrypto(
    CryptoId: string,
    CryptoData: Partial<Crypto>
  ): Promise<Crypto> {
    const bodyWithAppType = addAppTypeToBody(CryptoData)
    return makeApiRequest(`/crypto/coins/${CryptoId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bodyWithAppType),
    })
  }

  async deleteCrypto(CryptoId: string): Promise<void> {
    return makeApiRequest(`/crypto/coins/${CryptoId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
  }

  async bulkCreateCryptos(CryptosData: Crypto[]): Promise<any> {
    const bodyWithAppType = addAppTypeToBody(CryptosData)
    return makeApiRequest('/Cryptos/bulk-create', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bodyWithAppType),
    })
  }

  //========================================================== bOTTOM HAI cRYPTO uNIVERSSE =========================================================================================
  // Crypto Universe endpoints
  async getCryptoUniverses(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    active?: boolean
  ): Promise<CryptoUniverseListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    })

    if (search) params.append('search', search)
    if (active !== undefined) params.append('is_active', active.toString())

    const paramsWithAppType = addAppTypeToParams(params)

    return makeApiRequest(`/crypto-universes?${paramsWithAppType}`, {
      headers: this.getAuthHeaders(),
    })
  }

  async getStockById(cryptoId: number): Promise<CryptoUniverse> {
    return makeApiRequest(`/crypto-universes/${cryptoId}`, {
      headers: this.getAuthHeaders(),
    })
  }

  async getActiveCryptoUniverses(): Promise<{ universes: CryptoUniverse[] }> {
    return makeApiRequest('/crypto-universes', {
      headers: this.getAuthHeaders(),
    })
  }

  async createCryptoUniverse(
    universeData: Partial<CryptoUniverse>
  ): Promise<CryptoUniverse> {
    const bodyWithAppType = addAppTypeToBody(universeData)
    return makeApiRequest('/crypto-universes', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bodyWithAppType),
    })
  }

  async updateCryptoUniverse(
    universeId: string,
    universeData: Partial<CryptoUniverse>
  ): Promise<CryptoUniverse> {
    const bodyWithAppType = addAppTypeToBody(universeData)
    return makeApiRequest(`/crypto-universes/${universeId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bodyWithAppType),
    })
  }

  async deleteCryptoUniverse(universeId: string): Promise<void> {
    return makeApiRequest(`/crypto-universes/${universeId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
  }
}

export const cryptoService = new CryptoService()
